(() => {
  const initializeGalleries = (root = document) => {
    root.querySelectorAll('[data-kaede-product-gallery]').forEach((gallery) => {
      if (gallery.dataset.kaedeGalleryReady === 'true') return;

      const stage = gallery.querySelector('[data-kaede-gallery-stage]');
      const thumbnails = gallery.querySelectorAll('[data-kaede-gallery-thumbnail]');

      if (!stage || thumbnails.length === 0) return;

      gallery.dataset.kaedeGalleryReady = 'true';
      thumbnails.forEach((thumbnail) => {
        thumbnail.addEventListener('click', () => {
          stage.src = thumbnail.dataset.imageSrc;

          // srcset を残して、画面幅に見合う1枚を選ばせる。消すと常に最大幅を取りに行く。
          if (thumbnail.dataset.imageSrcset) {
            stage.srcset = thumbnail.dataset.imageSrcset;
          } else {
            stage.removeAttribute('srcset');
          }

          stage.alt = thumbnail.dataset.imageAlt;

          thumbnails.forEach((candidate) => {
            candidate.setAttribute('aria-pressed', String(candidate === thumbnail));
          });
        });
      });

      // 切替時にゼロから読むと待ちが出るため、残りの写真を先に取っておく。
      // 1枚目の表示と競合させないよう、読み込みが終わってアイドルになってから始める。
      const preloadRemainingImages = () => {
        thumbnails.forEach((thumbnail) => {
          const image = new Image();
          if (thumbnail.dataset.imageSrcset) {
            image.sizes = stage.sizes || '';
            image.srcset = thumbnail.dataset.imageSrcset;
          }
          image.src = thumbnail.dataset.imageSrc;
        });
      };

      const schedulePreload = () => {
        if ('requestIdleCallback' in window) {
          window.requestIdleCallback(preloadRemainingImages, { timeout: 3000 });
        } else {
          window.setTimeout(preloadRemainingImages, 1200);
        }
      };

      if (document.readyState === 'complete') {
        schedulePreload();
      } else {
        window.addEventListener('load', schedulePreload, { once: true });
      }
    });
  };

  const initializeProductForms = (root = document) => {
    root.querySelectorAll('[data-kaede-product-form]').forEach((productForm) => {
      if (productForm.dataset.kaedeProductFormReady === 'true') return;

      const form = productForm.querySelector('form');
      const submitButton = productForm.querySelector('[type="submit"]');
      const spinner = productForm.querySelector('.loading__spinner');
      const errorWrapper = productForm.querySelector('.product-form__error-message-wrapper');
      const errorMessage = productForm.querySelector('.product-form__error-message');

      if (!form || !submitButton) return;

      productForm.dataset.kaedeProductFormReady = 'true';
      form.addEventListener('submit', async (event) => {
        event.preventDefault();
        if (submitButton.disabled || submitButton.getAttribute('aria-disabled') === 'true') return;

        submitButton.setAttribute('aria-disabled', 'true');
        spinner?.classList.remove('hidden');
        errorWrapper?.setAttribute('hidden', '');

        try {
          const response = await fetch(window.routes.cart_add_url, {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: new FormData(form),
          });
          const result = await response.json();

          if (!response.ok || result.status) {
            throw new Error(result.description || result.message || 'Cart request failed');
          }

          window.location.assign(window.routes.cart_url);
        } catch (error) {
          if (errorMessage) errorMessage.textContent = error.message;
          errorWrapper?.removeAttribute('hidden');
          submitButton.removeAttribute('aria-disabled');
          spinner?.classList.add('hidden');
        }
      });
    });
  };

  const readProductPage = (productList, expected) => {
    const page = Number(productList.dataset.currentPage);
    const totalPages = Number(productList.dataset.totalPages);
    const totalItems = Number(productList.dataset.totalItems);
    const pageSize = Number(productList.dataset.pageSize);

    if (
      !Number.isInteger(page) ||
      !Number.isInteger(totalPages) ||
      !Number.isInteger(totalItems) ||
      !Number.isInteger(pageSize) ||
      page < 1 ||
      totalPages < 1 ||
      totalItems < 0 ||
      pageSize < 1 ||
      page > totalPages ||
      totalPages !== Math.max(1, Math.ceil(totalItems / pageSize)) ||
      (expected &&
        (page !== expected.page ||
          totalPages !== expected.totalPages ||
          totalItems !== expected.totalItems ||
          pageSize !== expected.pageSize))
    ) {
      throw new Error('Product pagination metadata did not match.');
    }

    const products = Array.from(productList.querySelectorAll('[data-kaede-product-card]')).map((card) => {
      const id = card.dataset.productId;
      const availableValue = card.dataset.productAvailable;
      const title = card.dataset.productTitle;

      if (!id || (availableValue !== 'true' && availableValue !== 'false') || title === undefined) {
        throw new Error('Product card metadata was incomplete.');
      }

      return {
        id,
        available: availableValue === 'true',
        title,
        card,
      };
    });

    const expectedProductCount = page < totalPages ? pageSize : Math.max(0, totalItems - pageSize * (totalPages - 1));
    if (products.length !== expectedProductCount) {
      throw new Error('Product count did not match the pagination metadata.');
    }

    return { page, totalPages, totalItems, pageSize, products };
  };

  const fetchProductPage = async (url, sectionId, expected) => {
    const pageUrl = new URL(url);
    pageUrl.searchParams.set('page', expected.page);
    pageUrl.searchParams.set('section_id', sectionId);

    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 8000);

    try {
      const response = await fetch(pageUrl, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        signal: controller.signal,
      });

      if (!response.ok) throw new Error(`Product page request failed with ${response.status}.`);

      const html = await response.text();
      const template = document.createElement('template');
      template.innerHTML = html;
      const productList = template.content.querySelector('[data-kaede-product-list]');

      if (!productList) throw new Error('Product list was missing from the section response.');

      return readProductPage(productList, expected).products;
    } finally {
      window.clearTimeout(timeout);
    }
  };

  const fetchProductPages = async (pageNumbers, sectionId, metadata) => {
    const results = new Array(pageNumbers.length);
    let nextIndex = 0;
    let failure;

    const worker = async () => {
      while (nextIndex < pageNumbers.length && !failure) {
        const index = nextIndex;
        nextIndex += 1;
        const page = pageNumbers[index];

        try {
          results[index] = await fetchProductPage(window.location.href, sectionId, { ...metadata, page });
        } catch (error) {
          failure = error;
        }
      }
    };

    const workerCount = Math.min(2, pageNumbers.length);
    await Promise.all(Array.from({ length: workerCount }, () => worker()));

    if (failure) throw failure;
    return results.flat();
  };

  const renderProductPage = (productList, products, metadata) => {
    const start = (metadata.page - 1) * metadata.pageSize;
    const pageProducts = products.slice(start, start + metadata.pageSize);
    const groups = {
      available: pageProducts.filter((product) => product.available),
      sold: pageProducts.filter((product) => !product.available),
    };

    const replacements = Object.entries(groups).map(([name, groupProducts]) => {
      const group = productList.querySelector(`[data-kaede-stock-group="${name}"]`);
      const grid = group?.querySelector('.kaede-product-grid');

      if (!group || !grid) throw new Error('Product stock group was missing.');

      const fragment = document.createDocumentFragment();
      groupProducts.forEach(({ card }) => fragment.append(card.cloneNode(true)));

      return { group, grid, fragment, isEmpty: groupProducts.length === 0 };
    });

    replacements.forEach(({ group, grid, fragment, isEmpty }) => {
      grid.replaceChildren(fragment);
      group.hidden = isEmpty;
    });

    const emptyMessage = productList.querySelector('.kaede-store-collection__empty');
    if (emptyMessage) emptyMessage.hidden = metadata.totalItems !== 0;
    productList.dataset.kaedeProductListReady = 'true';
  };

  const initializeProductLists = (root = document) => {
    root.querySelectorAll('[data-kaede-product-list]').forEach(async (productList) => {
      if (
        productList.dataset.kaedeProductListLoading === 'true' ||
        productList.dataset.kaedeProductListReady === 'true'
      ) {
        return;
      }

      const sectionId = productList.dataset.sectionId;
      if (!sectionId) return;

      productList.dataset.kaedeProductListLoading = 'true';
      productList.setAttribute('aria-busy', 'true');

      try {
        const current = readProductPage(productList);
        if (current.totalPages === 1) {
          productList.dataset.kaedeProductListReady = 'true';
          return;
        }

        const pageNumbers = Array.from({ length: current.totalPages }, (_, index) => index + 1).filter(
          (page) => page !== current.page,
        );
        const fetchedProducts = await fetchProductPages(pageNumbers, sectionId, current);
        const products = [...current.products, ...fetchedProducts];
        const ids = new Set(products.map((product) => product.id));

        if (products.length !== current.totalItems || ids.size !== current.totalItems) {
          throw new Error('Product count changed while pages were loading.');
        }

        const collator = new Intl.Collator(document.documentElement.lang || undefined, {
          numeric: true,
          sensitivity: 'base',
        });
        products.sort((left, right) => {
          if (left.available !== right.available) return left.available ? -1 : 1;
          return collator.compare(left.title, right.title) || left.id.localeCompare(right.id);
        });

        renderProductPage(productList, products, current);
      } catch (error) {
        console.warn('Kaede product list enhancement failed.', error);
      } finally {
        delete productList.dataset.kaedeProductListLoading;
        productList.removeAttribute('aria-busy');
      }
    });
  };

  const initializeStore = (root = document) => {
    initializeGalleries(root);
    initializeProductForms(root);
    initializeProductLists(root);
  };

  initializeStore();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeStore(), { once: true });
  }

  document.addEventListener('shopify:section:load', (event) => initializeStore(event.target));
})();
